'use client'

import { useState, useContext, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ThemeContext } from '@/src/contexts'
import { getUserProfile } from '@/src/api'

import {
  Avatar,
  Divider,
  Drawer,
  Layout,
  Menu,
  theme,
  Tooltip,
  Typography,
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import { LogoutButton, Sidebar, ThemeButton } from '@/src/components'
import { getClientIfNotExpired } from '@/src/utils'
import { navbarMenuItems } from '@/src/data'
import '@/src/lib'

import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
config.autoAddCss = false

const { Header } = Layout
const { Link } = Typography

const NavbarAvatar = () => {
  const router = useRouter()
  const [profilePicture, setProfilePicture] = useState<string>('')

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await getUserProfile()
        if (userData?.profile_picture) {
          setProfilePicture(userData.profile_picture)
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error)
      }
    }

    fetchProfile()
  }, [])

  return (
    <Tooltip
      title='My Profile'
      placement='bottom'
      trigger={['hover', 'focus']}
      className='cursor-pointer'
      zIndex={1000}
      mouseEnterDelay={0.5}
    >
      <Avatar
        {...(profilePicture
          ? { src: profilePicture }
          : { icon: <FontAwesomeIcon icon='user' /> })}
        alt='User Avatar'
        style={{
          width: '40px',
          height: '40px',
          marginRight: '16px',
          border: '3px solid #57ADB9',
        }}
        className='cursor-pointer hover:bg-[#57ADB9] hover:transition-all hover:duration-500'
        onClick={() => router.push('/my-profile')}
      />
    </Tooltip>
  )
}

export const Navbar = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { isDarkMode } = useContext(ThemeContext)
  const router = useRouter()

  const PhysicianGPSLogo = isDarkMode
    ? '/assets/logo/PhysicianGPSLogoDark.png'
    : '/assets/logo/PhysicianGPSLogo.png'

  const menuItems = navbarMenuItems
    ?.slice(0, -2)
    .filter(
      (item): item is { key: string; label: React.ReactNode } =>
        !!item && typeof item === 'object' && 'label' in item && 'key' in item
    )
    .map((item) => ({
      key: item.key,
      label: <span style={{ color: '#57adb9' }}>{item.label}</span>,
      // ...add other props like icon, children, etc. if needed
    }))

  const handleLogoClick = () => {
    router.push('/home')
  }

  /**
   * Get the client logo from the session
   * @returns the client logo URL
   */
  const clientLogo = () => {
    const logo =
      typeof window !== 'undefined'
        ? getClientIfNotExpired('client')?.logo_url
        : null
    return logo
  }

  return (
    <>
      <Drawer
        placement='right'
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        width={250}
        className='md:block xl:hidden'
        closeIcon={false}
      >
        <Sidebar onClose={() => setIsSidebarOpen(false)} />
      </Drawer>
      <Header
        data-testid='header'
        style={{
          background: colorBgContainer,
          height: 'auto',
        }}
        className='border-b border-[#d9d9d9ff] flex flex-col print:hidden'
      >
        <div className='flex justify-between items-center pt-5'>
          <div className='flex items-center'>
            <Link
              href='/home'
              className='flex items-center'
              aria-label='Home'
              onClick={handleLogoClick}
            >
              <Image
                src={PhysicianGPSLogo}
                alt='PhysicianGPS Logo'
                width={200}
                height={50}
                className='object-contain'
                style={{ maxHeight: '50px' }}
                priority
              />
            </Link>
            {clientLogo() && (
              <>
                <Divider
                  type='vertical'
                  style={{
                    background: 'rgba(217, 217, 217, 1)',
                    height: '30px',
                    margin: '0 16px',
                  }}
                  data-testid='vertical-divider'
                />
                <div className='flex items-center'>
                  <Image
                    src={clientLogo()}
                    alt={`Client Logo`}
                    width={200}
                    height={50}
                    className='w-auto h-auto max-h-[50px] object-contain'
                    priority
                  />
                </div>
              </>
            )}
          </div>
          <div>
            <div className='hidden xl:flex justify-center items-center print:hidden'>
              <Menu
                mode='horizontal'
                style={{ borderBottom: 'none', lineHeight: '30px' }}
                disabledOverflow={true}
                items={menuItems}
              />
              <NavbarAvatar />
              <ThemeButton />
              <LogoutButton />
            </div>
            <div className='flex flex-row justify-center items-center xl:hidden'>
              <button
                className='xl:hidden flex items-center justify-center p-2 border border-[#d9d9d9ff] rounded transition-colors hover:bg-[#f0f0f0] ml-2'
                aria-label='Open menu'
                data-testid='mobile-menu-icon'
                onClick={() => setIsSidebarOpen(true)}
                type='button'
              >
                <FontAwesomeIcon
                  icon='bars'
                  className='text-2xl text-[#06686b]'
                />
              </button>
            </div>
          </div>
        </div>
        <div className='justify-end items-center text-right print:hidden'>
          <Link
            href='/FAQs'
            className='text-[#26656B] hover:text-[#57adb9]'
            aria-label="FAQs - Couldn't find your physician?"
          >
            Couldn't find your physician? <FontAwesomeIcon icon='circle-info' />
          </Link>
        </div>
      </Header>
    </>
  )
}
